/*
 * Following components are based on the Shadcn UI documentation.
 * Visit: https://ui.shadcn.com/docs/components/typography#h1
 */

import React from 'react';
import { cn } from '@/lib/utils';

export type TypographyCdsProps = {
  children: React.ReactNode;
  className?: string;
};

type TypographyCdsPropsReadonly = Readonly<TypographyCdsProps>;

/**
 * H1Cds is a functional component that renders an H1 HTML element with predefined styles.
 * It accepts children and an optional className as props.
 *
 * @param {TypographyCdsProps} props - The props for the H1Cds component.
 * @param {React.ReactNode} props.children - The content to be displayed inside the H1 element.
 * @param {string} [props.className] - An optional additional className to apply to the H1 element.
 * @returns {JSX.Element} The rendered H1 element with the provided children and className.
 *
 * @example
 * <H1Cds className="custom-class">This is an H1 heading example.</H1Cds>
 */
export function H1Cds({ children, className }: TypographyCdsPropsReadonly) {
  return (
    <h1 className={cn('scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl', className)}>
      {children}
    </h1>
  );
}

/**
 * A functional component that renders an H2 element with predefined styles and additional class names.
 *
 * @param {TypographyCdsProps} props - The properties object.
 * @param {React.ReactNode} props.children - The content to be displayed inside the H2 element.
 * @param {string} [props.className] - Additional class names to apply to the H2 element.
 * @returns {JSX.Element} The rendered H2 element with the specified content and styles.
 *
 * @example
 * <H2Cds className="custom-class">This is an H2 heading example.</H2Cds>
 */
export function H2Cds({ children, className }: TypographyCdsPropsReadonly) {
  return (
    <h2
      className={cn(
        'scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0',
        className
      )}
    >
      {children}
    </h2>
  );
}

/**
 * A functional component that renders an H3 heading with predefined styles.
 *
 * @param {TypographyCdsProps} props - The properties for the H3Cds component.
 * @param {React.ReactNode} props.children - The content to be displayed inside the H3 element.
 * @param {string} [props.className] - Additional class names to apply to the H3 element.
 *
 * @returns {JSX.Element} The rendered H3 element with the specified children and class names.
 *
 * @example
 * <H3Cds className="custom-class">This is an H3 heading example.</H3Cds>
 */
export function H3Cds({ children, className }: TypographyCdsPropsReadonly) {
  return (
    <h3 className={cn('scroll-m-20 text-2xl font-semibold tracking-tight', className)}>
      {children}
    </h3>
  );
}

/**
 * A functional component that renders an H4 heading with predefined styles.
 *
 * @param {TypographyCdsProps} props - The properties for the H4Cds component.
 * @param {React.ReactNode} props.children - The content to be displayed inside the H4 element.
 * @param {string} [props.className] - Additional class names to apply to the H4 element.
 *
 * @returns {JSX.Element} The rendered H4 element with the specified children and class names.
 *
 * @example
 * <H4Cds className="custom-class">This is an H4 heading example.</H4Cds>
 */
export function H4Cds({ children, className }: TypographyCdsPropsReadonly) {
  return (
    <h4 className={cn('scroll-m-20 text-xl font-semibold tracking-tight', className)}>
      {children}
    </h4>
  );
}

/**
 * A functional component that renders a paragraph (`<p>`) element with custom styles.
 *
 * @param {TypographyCdsProps} props - The properties object.
 * @param {React.ReactNode} props.children - The content to be displayed inside the paragraph.
 * @param {string} [props.className] - Additional class names to apply to the paragraph element.
 *
 * @returns {JSX.Element} A paragraph element with the specified children and class names.
 *
 * @example
 * <PCds className="custom-class">This is a paragraph example.</PCds>
 */
export function PCds({ children, className }: TypographyCdsPropsReadonly) {
  return <p className={cn('leading-7 [&:not(:first-child)]:mt-6', className)}>{children}</p>;
}

/**
 * Renders a blockquote element with custom styling.
 *
 * @param {TypographyCdsProps} props - The properties for the BlockquoteCds component.
 * @param {React.ReactNode} props.children - The content to be displayed inside the blockquote.
 * @param {string} [props.className] - Additional class names to apply to the blockquote element.
 *
 * @example
 * <BlockquoteCds className="custom-class">
 *   "After all," he said, "everyone enjoys a good joke, so it's only fair that they should pay for the privilege."
 * </BlockquoteCds>
 */
export function BlockquoteCds({ children, className }: TypographyCdsPropsReadonly) {
  return (
    <blockquote className={cn('mt-6 border-l-2 pl-6 italic', className)}>{children}</blockquote>
  );
}

/**
 * A functional component that renders a list with custom styles.
 *
 * @param {TypographyCdsProps} props - The properties for the ListCds component.
 * @param {React.ReactNode} props.children - The list items to be rendered inside the list.
 * @param {string} [props.className] - Additional class names to apply to the list.
 *
 * @returns {JSX.Element} A styled unordered list element.
 *
 * @example
 * <ListCds className="custom-class">
 *   <li>Item 1</li>
 *   <li>Item 2</li>
 *   <li>Item 3</li>
 * </ListCds>
 */
export function ListCds({ children, className }: TypographyCdsPropsReadonly) {
  return <ul className={cn('my-6 ml-6 list-disc [&>li]:mt-2', className)}>{children}</ul>;
}

/**
 * A React functional component that renders inline code with custom styling.
 *
 * @param {TypographyCdsProps} props - The properties for the component.
 * @param {React.ReactNode} props.children - The content to be displayed inside the inline code block.
 * @param {string} [props.className] - Additional class names to apply to the code block.
 *
 * @returns {JSX.Element} The rendered inline code block with the provided children and class names.
 *
 * @example
 * <InlineCodeCds className="custom-class">const x = 10;</InlineCodeCds>
 */
export function InlineCodeCds({ children, className }: TypographyCdsPropsReadonly) {
  return (
    <code
      className={cn(
        'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
        className
      )}
    >
      {children}
    </code>
  );
}

/**
 * LeadCds component renders a paragraph element with specific styles.
 *
 * @param {TypographyCdsProps} props - The properties for the LeadCds component.
 * @param {React.ReactNode} props.children - The content to be displayed within the paragraph.
 * @param {string} [props.className] - Additional class names to apply to the paragraph element.
 *
 * @returns {JSX.Element} A paragraph element with the specified styles and content.
 *
 * @example
 * <LeadCds className="custom-class">This is a lead text example.</LeadCds>
 */
export function LeadCds({ children, className }: TypographyCdsPropsReadonly) {
  return <p className={cn('text-xl text-muted-foreground', className)}>{children}</p>;
}

/**
 * A functional component that renders its children inside a <div> HTML element
 * with predefined styles for large text size and font weight.
 *
 * @param {TypographyCdsProps} props - The props for the component.
 * @param {React.ReactNode} props.children - The content to be rendered inside the <div> element.
 * @param {string} [props.className] - Additional class names to apply to the <div> element.
 * @returns {JSX.Element} The rendered <div> element with the specified children and class names.
 *
 * @example
 * <LargeCds className="custom-class">This is a large text example.</LargeCds>
 */
export function LargeCds({ children, className }: TypographyCdsPropsReadonly) {
  return <div className={cn('text-lg font-semibold', className)}>{children}</div>;
}

/**
 * A functional component that renders its children inside a <small> HTML element
 * with predefined styles for text size, font weight, and line height.
 *
 * @param {TypographyCdsProps} props - The props for the component.
 * @param {React.ReactNode} props.children - The content to be rendered inside the <small> element.
 * @param {string} [props.className] - Additional class names to apply to the <small> element.
 * @returns {JSX.Element} The rendered <small> element with the specified children and class names.
 *
 * @example
 * <SmallCds className="custom-class">This is a small text example.</SmallCds>
 */
export function SmallCds({ children, className }: TypographyCdsPropsReadonly) {
  return <small className={cn('text-sm font-medium leading-none', className)}>{children}</small>;
}

/**
 * A functional component that renders its children inside a paragraph (`<p>`) element
 * with a muted text style. The component accepts additional class names to be applied
 * to the paragraph element.
 *
 * @param {TypographyCdsProps} props - The properties object.
 * @param {React.ReactNode} props.children - The content to be rendered inside the paragraph element.
 * @param {string} [props.className] - Additional class names to be applied to the paragraph element.
 *
 * @returns {JSX.Element} A paragraph element with muted text style and any additional class names.
 *
 * @example
 * <MutedCds className="custom-class">This is a muted text example.</MutedCds>
 */
export function MutedCds({ children, className }: TypographyCdsPropsReadonly) {
  return <p className={cn('text-sm text-muted-foreground', className)}>{children}</p>;
}
